import { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';
import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { PagerTargetEvent } from '@progress/kendo-react-data-tools';
import {
    Grid,
    GridColumn,
    GridColumnMenuFilter,
    GridColumnMenuProps,
    GridColumnMenuSort,
    GridCustomCellProps,
    GridFilterChangeEvent,
    GridPageChangeEvent,
    GridSortChangeEvent
} from '@progress/kendo-react-grid';
import { Checkbox, NumericTextBox } from '@progress/kendo-react-inputs';
import { useEffect, useState } from 'react';
import Coordinate from '../../../../common/coordinate';
import { MinimalAnalyzerRoute } from '../../../../components/route-analyzer/types';
import { FlightDetailsModel } from '../../../../flights/interop/flightDto';
import { UnitFormatter } from '../../../../formatters';
import useFilterGroup from '../../../../hooks/use-filter-group';
import { ArrowRightOutline, RadarOutline, SearchOutline } from '../../../../icons/icons';
import { GetNearestAirportsRequest, MessageType } from '../../../../services/actor-messages';
import { AirportWithDistance } from '../../../../static/static-airports';
import { FilterGroupType } from '../../../../types/filter-group';
import FlightStatusBadge from '../../../components/FlightStatusBadge';
import { DropdownListWithValueField } from '../../../components/inputs/DropdownListWithValueField';
import Location from '../../../components/inputs/Location';
import { EventBus } from '../../../contexts/event-bus';
import { useFlightWorker } from '../../../hooks/use-flight-worker';
import { createAirportExclusionFilters, getId } from '../../../utils/airport';
import { toReadableHeading } from '../../../utils/bearing';
import { useFormNumericValue, useNullableFilterGroupDropdownValue } from '../hooks/forms';
import { CityLocationDetails } from '../hooks/use-flight-position';
import { useNearestCity } from '../hooks/use-nearest-city';

const initialMaxDistance = 200;
const initialMinRunwayLength = 7000;

export default function EmergencyDiversion(props: { flightId: number | null }) {
    const { getNearestCity } = useNearestCity();
    const [nearestCity, setNearestCity] = useState<CityLocationDetails | null>(null);
    const [nearestAirports, setNearestAirports] = useState<AirportWithDistance[]>([]);
    const [selectedAirports, setSelectedAirports] = useState<string[]>([]);
    const worker = useFlightWorker();

    const [flight, setFlight] = useState<FlightDetailsModel | null>(null);

    useEffect(() => {
        const fetchFlight = async () => {
            if (props.flightId === null) {
                setFlight(null);
                return;
            }
            const x = await worker.actor.sendAsync({
                type: MessageType.getFlight,
                data: props.flightId
            });
            setFlight(x);
        };

        fetchFlight();
    }, [props.flightId, worker]);

    const calculatePageSize = (): number => {
        const height = window.innerHeight;
        if (height < 700) {
            return 5;
        }
        if (height < 900) {
            return 10;
        }
        if (height < 1100) {
            return 15;
        }
        return 25;
    };
    const [pageSizeValue, setPageSizeValue] = useState<number>(calculatePageSize());
    const initialDataState: State = { skip: 0, take: calculatePageSize() };
    const [state, setState] = useState<State>(initialDataState);
    const [total, setTotal] = useState<number | undefined>();

    const maxDistance = useFormNumericValue(initialMaxDistance);
    const minRunwayLength = useFormNumericValue(initialMinRunwayLength);
    const filterGroup = useNullableFilterGroupDropdownValue(FilterGroupType.airport);
    const filterGroups = useFilterGroup(FilterGroupType.airport);

    useEffect(() => {
        const fetchNearestCity = async () => {
            if (flight?.position) {
                const city = await getNearestCity(flight.position);
                setNearestCity(city);
            }
        };

        fetchNearestCity();
    }, [getNearestCity]);

    const fetchNearestAirports = async () => {
        if (!flight?.position) {
            return;
        }

        const filters: (CompositeFilterDescriptor | FilterDescriptor)[] = [];
        if (maxDistance.value !== null) {
            filters.push({ field: 'distance', operator: 'lte', value: UnitFormatter.convertDistanceToNm(maxDistance.value) });
        }
        if (minRunwayLength.value !== null) {
            filters.push({ field: 'runwayLength', operator: 'gte', value: minRunwayLength.value });
        }
        if (filterGroup.value) {
            const filterGroupFilters = [];
            for (const id of filterGroups.find((fg) => fg.id === filterGroup.value)?.members.map((m) => m.text) ?? []) {
                filterGroupFilters.push({ field: 'icao', operator: 'eq', value: id });
                filterGroupFilters.push({ field: 'iata', operator: 'eq', value: id });
                filterGroupFilters.push({ field: 'faa', operator: 'eq', value: id });
            }
            filters.push({
                logic: 'or',
                filters: filterGroupFilters
            });
        }

        filters.push(createAirportExclusionFilters([{ icao: flight?.origin ?? undefined }, { icao: flight?.destination ?? undefined }]));

        const effectiveFilters = {
            logic: 'and',
            filters: [...(state.filter ? (state.filter as CompositeFilterDescriptor).filters : []), ...filters]
        };
        const result = await worker.actor.sendAsync({
            type: MessageType.getNearestAirports,
            data: {
                lat: flight.position.lat,
                lng: flight.position.lng,
                state: { ...state, filter: effectiveFilters }
            } as GetNearestAirportsRequest
        });
        setNearestAirports(result.data as AirportWithDistance[]);
        setTotal(result.total);
    };

    useEffect(() => {
        fetchNearestAirports();
    }, [state]);

    const getInitialAirports = () => {
        setState(initialDataState);
        setSelectedAirports([]);
        fetchNearestAirports();
    };

    const pageChange = (event: GridPageChangeEvent) => {
        const targetEvent = event.targetEvent as PagerTargetEvent;
        if (targetEvent.value) {
            setPageSizeValue(targetEvent.value);
        }
        setState({
            ...state,
            skip: event.page.skip,
            take: event.page.take
        });
    };

    const sortChange = (event: GridSortChangeEvent) => {
        setState({
            ...state,
            sort: event.sort
        });
    };

    const filterChange = (event: GridFilterChangeEvent) => {
        setState({
            ...state,
            filter: event.filter
        });
    };

    const openSelectedRoutesInRouteAnalyzer = () => {
        if (!flight) {
            return;
        }

        const selected = nearestAirports.filter((x) => selectedAirports.includes(getId(x)));
        const routes = selected.map((airport) => toAnalyzerRoute(airport, flight));
        EventBus.fire('route-analyzer-load-routes', routes);
    };

    const openRouteInRouteAnalyzer = (airport: AirportWithDistance) => {
        if (!flight) {
            return;
        }

        const route = toAnalyzerRoute(airport, flight);
        EventBus.fire('route-analyzer-load-routes', [route]);
    };

    if (!flight) {
        return <></>;
    }

    return (
        <div className="h-100 min-h-0 p-2 d-flex flex-column">
            <div className={'d-flex justify-content-between pb-3 pt-1 border-bottom'}>
                <div>
                    <div className="secondary-text-color k-font-size-sm">Aircraft ID:</div>
                    <div>{flight.acid}</div>
                </div>
                <div>
                    <div className="secondary-text-color k-font-size-sm">Status:</div>
                    <div className={'d-flex align-items-center gap-2'}>
                        <FlightStatusBadge flightStatus={flight.status} ground={flight.ground} />
                        <span className={'d-flex align-items-center gap-1'}>
                            <span>{flight.origin ?? 'N/A'}</span>
                            <SvgIcon icon={ArrowRightOutline} />
                            <span>{flight.destination ?? 'N/A'}</span>
                        </span>
                    </div>
                </div>

                <div>
                    <div className="secondary-text-color k-font-size-sm">Location:</div>
                    <div>
                        <Location coordinates={flight.position} clickable={true} />
                    </div>
                </div>

                <div>
                    <div className="secondary-text-color k-font-size-sm">Closest city:</div>
                    {nearestCity && (
                        <div>
                            {UnitFormatter.formatDistance(nearestCity?.distance ?? null)}{' '}
                            {toReadableHeading(nearestCity?.fromBearing ?? null)} of {nearestCity?.name}
                        </div>
                    )}
                    {!nearestCity && <div className="ms-1">N/A</div>}
                </div>
            </div>
            <div className={'d-flex gap-3 pt-3 pb-3 justify-content-between'}>
                <div>
                    <div>Maximum distance ({UnitFormatter.distanceUnit})</div>
                    <NumericTextBox {...maxDistance} rounded={'large'} />
                </div>
                <div>
                    <div>Minimum runway length (ft)</div>
                    <NumericTextBox {...minRunwayLength} rounded={'large'} />
                </div>

                <div>
                    <div>Filter group (optional)</div>
                    <DropdownListWithValueField {...filterGroup} rounded={'large'} />
                </div>
            </div>
            <div className={'d-flex gap-2 pb-3'}>
                <Button
                    className={'button-small w-50'}
                    fillMode={'solid'}
                    themeColor={'primary'}
                    svgIcon={SearchOutline}
                    onClick={() => {
                        getInitialAirports();
                    }}>
                    Find matching airports
                </Button>
                <Button
                    className={'button-small w-50'}
                    fillMode={'solid'}
                    disabled={selectedAirports.length === 0}
                    themeColor={'tertiary'}
                    svgIcon={RadarOutline}
                    onClick={() => {
                        openSelectedRoutesInRouteAnalyzer();
                    }}>
                    Show in Route Analyzer {selectedAirports.length > 0 ? `(${selectedAirports.length})` : ''}
                </Button>
            </div>

            <Grid
                style={{ minHeight: 350 }}
                className={'h-100 min-h-0 grid-hide-filter-row telerik-cobaltify'}
                data={nearestAirports}
                rowHeight={28}
                sortable={{ mode: 'single' }}
                {...state}
                total={total}
                pageable={{
                    buttonCount: 4,
                    pageSizes: [5, 10, 15, 25],
                    pageSizeValue: pageSizeValue
                }}
                onPageChange={pageChange}
                onSortChange={sortChange}
                onFilterChange={filterChange}>
                <GridColumn
                    width={30}
                    cells={{
                        data: (props: GridCustomCellProps) => {
                            return (
                                <td {...props.tdProps} className="k-command-cell">
                                    <Checkbox
                                        checked={selectedAirports.includes(getId(props.dataItem as AirportWithDistance))}
                                        onChange={(e) => {
                                            const item = props.dataItem as AirportWithDistance;
                                            if (e.value) {
                                                setSelectedAirports((prev) => [...prev, getId(item)]);
                                            } else {
                                                setSelectedAirports((prev) => prev.filter((x) => x !== getId(item)));
                                            }
                                        }}
                                    />
                                </td>
                            );
                        }
                    }}
                />
                <GridColumn
                    field="icao"
                    title={'Id'}
                    columnMenu={ColumnMenu}
                    width={70}
                    cells={{
                        data: (props: GridCustomCellProps) => {
                            const dataItem = props.dataItem as AirportWithDistance;
                            return <td {...props.tdProps}>{getId(dataItem)}</td>;
                        }
                    }}
                />
                <GridColumn field="runwayLength" columnMenu={ColumnMenu} filter={'numeric'} title="RWay (ft)" />
                <GridColumn
                    field="distance"
                    columnMenu={ColumnMenu}
                    filter={'numeric'}
                    title={`Dist (${UnitFormatter.distanceUnit})`}
                    format={'{0:n2}'}
                />
                <GridColumn field="bearing" columnMenu={ColumnMenu} filter={'numeric'} title="Bearing" />
                <GridColumn
                    field="name"
                    columnMenu={ColumnMenu}
                    title="Name"
                    className={'text-truncate text-nowrap'}
                    cells={{
                        data: (props: GridCustomCellProps) => {
                            const dataItem = props.dataItem as AirportWithDistance;
                            return (
                                <td {...props.tdProps} title={dataItem.name}>
                                    {dataItem.name}
                                </td>
                            );
                        }
                    }}
                />
                <GridColumn
                    field="country"
                    columnMenu={ColumnMenu}
                    title="Location"
                    className={'text-truncate text-nowrap'}
                    cells={{
                        data: (props: GridCustomCellProps) => {
                            const dataItem = props.dataItem as AirportWithDistance;
                            return (
                                <td {...props.tdProps} title={dataItem.country}>
                                    {dataItem.country}
                                </td>
                            );
                        }
                    }}
                />
                <GridColumn
                    width={50}
                    cells={{
                        data: (props: GridCustomCellProps) => {
                            const dataItem = props.dataItem as AirportWithDistance;
                            return (
                                <td {...props.tdProps} className="k-command-cell">
                                    <Button
                                        className={'button-small'}
                                        style={{ width: 22, height: 22 }}
                                        fillMode="outline"
                                        svgIcon={RadarOutline}
                                        title="Show in Route Analyzer"
                                        onClick={() => openRouteInRouteAnalyzer(dataItem)}
                                    />
                                </td>
                            );
                        }
                    }}
                />
            </Grid>
        </div>
    );
}

const ColumnMenu = (props: GridColumnMenuProps) => {
    return (
        <div>
            <GridColumnMenuSort {...props} />
            <GridColumnMenuFilter {...props} />
        </div>
    );
};

function toAnalyzerRoute(airport: AirportWithDistance, flight: FlightDetailsModel): MinimalAnalyzerRoute {
    const positionCoordinate = Coordinate.fromLatLng(flight.position.lat, flight.position.lng);
    const route: MinimalAnalyzerRoute = {
        label: getId(airport),
        origin: UnitFormatter.formatLatLng(positionCoordinate),
        destination: getId(airport),
        route: `${positionCoordinate.toRouteString()} ${getId(airport)}`,
        distance: airport.distance,
        sequence: null,
        type: null
    };
    return route;
}
